import React, { useMemo, useEffect, useCallback } from "react";
import { connect } from "react-redux";
import moment from "moment";

import ContractUtils from "services/contract/utils";
import LiveActioner from "services/live/actioner";
import LiveUtils from "services/live/utils";
import MapActioner from "services/map/actioner";
import useLiveReload from "services/hooks/useLiveReload";

import FullPageLayout from "components/FullPageLayout";
import Map from "components/Map";
import MapComponent from "./components/MapComponent";
import Filters from "./components/Filters";
import { AlertModal } from "./components/AlertModals";

function Live({
  currentFloor,
  currentFloorLayers,
  currentFilters,
  map,
  retrieveMapData,
  retrieveAllParkDetailsStats,
  retrieveAllPedestrianDetailsStats,
  retrieveFlowDetailsStats,
  retrieveAllFlowDetailsStats,
  retrieveAllFlowGeneralStats,
  retrieveFlowGeneralStats,
  retrieveDisplays,
  setAutoRefresh,
  setFilter,
  setSelectedLayers,
  selectedLayers,
  setCenterToDefault,
  parkedCars,
  availableParking,
  retrieveAlertEvents,
}) {
  const secondsFromLive = useMemo(() => {
    const momentFromFilters = moment(currentFilters?.timestamp * 1000);
    return moment().diff(momentFromFilters, "seconds");
  }, [currentFilters?.timestamp]);

  const initializeData = useCallback(() => {
    retrieveDisplays();
    retrieveAllParkDetailsStats();
    retrieveAllPedestrianDetailsStats();
    retrieveAllFlowDetailsStats();
    retrieveAllFlowGeneralStats();
    retrieveMapData();
    retrieveAlertEvents();
  }, [
    retrieveDisplays,
    retrieveMapData,
    retrieveAllFlowDetailsStats,
    retrieveAllFlowGeneralStats,
    retrieveAllParkDetailsStats,
    retrieveAllPedestrianDetailsStats,
    retrieveAlertEvents,
  ]);

  const handleTimestampChange = useCallback(
    (timestamp) => {
      setFilter("timestamp", timestamp);

      initializeData();
    },
    [initializeData, setFilter]
  );

  useEffect(() => {
    if (map?.initialized) {
      handleReloadOnce();
    }
  }, [currentFloor, map?.initialized]);

  const handleReloadOnce = useCallback(() => {
    const timestamp = Math.round(new Date().getTime() / 1000);
    handleTimestampChange(timestamp);
  }, [handleTimestampChange]);

  const currentFloorSelectedLayers = useMemo(() => {
    const currentSelectedLayers = selectedLayers?.filter(
      (layer) => layer?.floor_level === currentFloor
    );

    return currentSelectedLayers;
  }, [selectedLayers, currentFloor]);

  const toggleSpaceLabels = useMemo(() => {
    return (
      parkedCars?.items?.some(
        (car) => car?.meta_data?.client?.label !== undefined
      ) ||
      availableParking?.items?.some(
        (spot) => spot?.meta_data?.client?.label !== undefined
      )
    );
  }, [parkedCars, availableParking, map]);

  const handleSetAutoRefresh = useCallback(() => {
    setAutoRefresh(true);
  }, [setAutoRefresh]);

  const handleRecenter = useCallback(
    (mapRef) => {
      if (!currentFloorSelectedLayers?.length || !mapRef) {
        return setCenterToDefault();
      }
      const coordinates = [
        ...[
          ...currentFloorLayers?.paths,
          ...currentFloorLayers?.points,
        ]?.filter((c) => c.apiType === "VIRTUAL"),
        ...currentFloorSelectedLayers,
      ].reduce((coordinates, currentLayer) => {
        switch (currentLayer?.type) {
          case "area":
            return [...coordinates, currentLayer.polygon];
          case "point":
            return [...coordinates, currentLayer.location];
          case "path":
            return [...coordinates, currentLayer.polylines];
          default:
            return coordinates;
        }
      }, []);

      mapRef?.fitBounds(coordinates, {
        padding: [50, 50],
      });
    },
    [currentFloorSelectedLayers, setCenterToDefault]
  );

  const { liveReloadActive, onActivateLiveReload, onCancelLiveReload } =
    useLiveReload({
      key: "LIVE_PAGE_LIVE_RELOAD_INTERVAL_ID",
      onSetAutoRefresh: handleSetAutoRefresh,
      onReload: handleReloadOnce,
    });

  function handleAutoReloadChecked(checked) {
    const callback = checked ? onActivateLiveReload : onCancelLiveReload;
    return callback();
  }

  function handleSelectLayers(layers) {
    let updatedLayers = [...layers];

    const newlySelectedLayers = layers?.filter((layer) => {
      return !currentFloorSelectedLayers.some(
        (currentFloorLayer) => currentFloorLayer.id === layer.id
      );
    });

    // If some freshly unselected area FLOW or FLOWPARK, we should unselect there points
    const newlyUnselectedFlowOrFlowParkAreas = currentFloorSelectedLayers
      ?.filter((layer) => {
        return !layers.some(
          (currentFloorLayer) => currentFloorLayer.id === layer.id
        );
      })
      .filter((layer) => layer?.type === "area")
      .filter(
        (layer) =>
          ["FLOW", "PARKANDFLOW", "PEDESTRIANANDFLOW"].indexOf(
            layer?.apiType
          ) >= 0
      );

    if (newlyUnselectedFlowOrFlowParkAreas?.length > 0) {
      const pointsToUnselect = newlyUnselectedFlowOrFlowParkAreas.reduce(
        (previousPoints, currentArea) => {
          return [...previousPoints, ...(currentArea?.ref_flow_points || [])];
        },
        []
      );

      // We should unselect the linked to area points
      updatedLayers = layers.filter((layer) => {
        return pointsToUnselect?.indexOf(layer.id) < 0;
      });
    }

    setSelectedLayers(updatedLayers);
    newlySelectedLayers.forEach(handleRefreshLayerData);
  }

  function handleRefreshLayerData(layer) {
    if (layer?.floor_level !== currentFloor) {
      return null;
    }

    if (layer?.type === "area") {
      retrieveMapData();

      retrieveFlowDetailsStats(layer);
      retrieveAllParkDetailsStats();
      retrieveAllPedestrianDetailsStats();
    }
    if (layer.type === "point") {
      retrieveFlowDetailsStats(layer);
    }

    if (layer.type === "path") {
      retrieveFlowGeneralStats(layer);
    }
  }

  return (
    <FullPageLayout>
      <Map
        recenterButton
        daysHoursSlider
        floorsButton
        getSliderMaxDaysAllowed={ContractUtils.getParkMaxDaysAllowed}
        selectedTimestamp={currentFilters?.timestamp}
        onTimestampChange={handleTimestampChange}
        onRecenter={handleRecenter}
        mapComponent={
          <MapComponent
            onRecenter={handleRecenter}
            onSelectLayers={handleSelectLayers}
            isLive={secondsFromLive < 5}
          />
        }
        withCheckboxOptions={{
          aisles: false,
          displays: map?.displays?.length > 0,
          spaceLabels: toggleSpaceLabels,
        }}
      />

      <Filters
        loading={!map?.initialized}
        liveReloadActive={liveReloadActive}
        onReloadOnce={handleReloadOnce}
        onAutoReloadChecked={handleAutoReloadChecked}
        onSelectLayers={handleSelectLayers}
        currentFloorLayers={currentFloorLayers}
      />
      <AlertModal />
    </FullPageLayout>
  );
}

const mapStateToProps = (state) => ({
  map: state.Live?.map,
  currentFilters: state.Live.filters,
  currentFloorLayers: LiveUtils.formatLayersFromCurrentFloor(state),
  selectedLayers: state.Live?.map?.selectedLayers,
  currentFloor: state.Map?.config?.currentFloor,
  parkedCars: state?.Live?.parkedCars,
  availableParking: state?.Live?.availableParking,
});

const mapDispatchToProps = (dispatch) => ({
  setCenterToDefault: () => dispatch(MapActioner.setCenterToDefault()),
  retrieveAllFlowDetailsStats: () =>
    dispatch(LiveActioner.retrieveAllFlowDetailsStats()),
  retrieveAllFlowGeneralStats: () =>
    dispatch(LiveActioner.retrieveAllFlowGeneralStats()),
  retrieveAllParkDetailsStats: () =>
    dispatch(LiveActioner.retrieveAllParkDetailsStats()),
  retrieveAllPedestrianDetailsStats: () =>
    dispatch(LiveActioner.retrieveAllPedestrianDetailsStats()),
  retrieveMapData: () => dispatch(MapActioner.retrieveMapDataForLiveTab()),
  setAutoRefresh: (autoRefresh) =>
    dispatch(LiveActioner.setAutoRefresh(autoRefresh)),
  setFilter: (key, value) => dispatch(LiveActioner.setFilter(key, value)),
  setSelectedLayers: (layers) =>
    dispatch(LiveActioner.setSelectedLayers(layers)),
  retrieveFlowDetailsStats: (layer) =>
    dispatch(LiveActioner.retrieveFlowDetailsStats(layer)),
  retrieveFlowGeneralStats: (layer) =>
    dispatch(LiveActioner.retrieveFlowGeneralStats(layer)),
  retrieveDisplays: () => dispatch(LiveActioner.retrieveDisplays()),
  retrieveAlertEvents: () => dispatch(LiveActioner.retrieveAlertEvents()),
});

export default connect(mapStateToProps, mapDispatchToProps)(Live);
